type ConfigMap = Record<string, any>;
type ScopedSection = 'webchat' | 'extension';

const SCOPED_SECTIONS: ScopedSection[] = ['webchat', 'extension'];
const SCOPED_KEY_REGEX = /^space:([^:]+):(webchat|extension):(.+)$/;
const SCOPED_OBJECT_REGEX = /^space:([^:]+):(webchat|extension)$/;
const LANG_KEY_REGEX = /^[a-z]{2,3}$/;

const applySectionObjectAliases = (
  configMap: ConfigMap,
  scope: ScopedSection
): number => {
  const scopedObject = configMap[scope];
  if( !scopedObject || typeof scopedObject !== 'object' || Array.isArray( scopedObject ) )
    return 0;

  let applied = 0;
  Object.entries( scopedObject as Record<string, unknown> ).forEach( ( [nestedKey, nestedValue] ) => {
    if( !nestedKey )
      return;

    configMap[nestedKey] = nestedValue;
    applied += 1;
  } );

  return applied;
};

const applyAliasesForScope = (
  configMap: ConfigMap,
  scope: ScopedSection,
  spaceId: string
): number => {
  const prefix = `space:${spaceId}:${scope}:`;
  let applied = 0;

  Object.keys( configMap ).forEach( ( key ) => {
    if( !key.startsWith( prefix ) )
      return;

    const unscopedKey = key.substring( prefix.length );
    if( !unscopedKey )
      return;

    // Scoped values should win over global fallbacks — pero una CADENA VACÍA no
    // es un override, es ausencia de valor.
    //
    // El panel crea filas del space vacías al guardar (p.ej. `company-logo: ""`
    // cuando el formulario no llegó a cargar el valor). Promoverlas TAPABA el
    // global: el widget de Tactical Center perdió el logo de su cuenta —que
    // existe y está en `core_accounts.account_logo_url`— y cayó al orbe. Pasó
    // el 31-jul, se limpió a mano, y volvió a pasar el 03-ago con el siguiente
    // guardado del panel.
    //
    // "Sin logo" tiene su propio control (`chat-colors['welcome-avatar']`), así
    // que blanquear la URL no es la forma de expresar esa intención. Solo se
    // ignora la cadena vacía: `false`, `0` y `[]` son valores legítimos y
    // siguen ganando (`[]` es cómo se dice "sin dominios restringidos").
    const scopedValue = configMap[key];
    const globalValue = configMap[unscopedKey];
    const scopedIsEmptyString = typeof scopedValue === 'string' && scopedValue.trim() === '';
    const globalHasValue = globalValue !== undefined && globalValue !== null
      && !( typeof globalValue === 'string' && globalValue.trim() === '' );

    if( scopedIsEmptyString && globalHasValue )
      return;

    configMap[unscopedKey] = scopedValue;
    applied += 1;
  } );

  return applied;
};

const applyObjectAliasesForScope = (
  configMap: ConfigMap,
  scope: ScopedSection,
  spaceId: string
): number => {
  const key = `space:${spaceId}:${scope}`;
  const scopedObject = configMap[key];
  if( !scopedObject || typeof scopedObject !== 'object' || Array.isArray( scopedObject ) )
    return 0;

  let applied = 0;
  Object.entries( scopedObject as Record<string, unknown> ).forEach( ( [nestedKey, nestedValue] ) => {
    if( !nestedKey ) return;
    configMap[nestedKey] = nestedValue;
    applied += 1;
  } );

  return applied;
};

const applyLanguageAliases = (
  configMap: ConfigMap
): number => {
  let applied = 0;

  Object.entries( configMap ).forEach( ( [key, value] ) => {
    if( !key )
      return;

    if( !value || typeof value !== 'object' || Array.isArray( value ) )
      return;

    const valueObject = value as Record<string, unknown>;
    const nestedKeys = Object.keys( valueObject );
    const isLanguageMap = nestedKeys.length > 0 && nestedKeys.every( nestedKey => LANG_KEY_REGEX.test( nestedKey ) );

    if( !isLanguageMap )
      return;

    nestedKeys.forEach( lang => {
      configMap[`${key}-${lang}`] = valueObject[lang];
      applied += 1;
    } );
  } );

  return applied;
};

/**
 * Resolve scoped config keys (space:{uuid}:{scope}:*) into unscoped aliases.
 * Supports explicit candidate IDs and a safe fallback when only one space scope exists.
 */
export function resolveScopedConfigAliases(
  configMap: ConfigMap,
  candidateSpaceIds: Array<string | null | undefined> = []
): void {
  if( !configMap || typeof configMap !== 'object' )
    return;

  SCOPED_SECTIONS.forEach( ( scope ) => {
    applySectionObjectAliases( configMap, scope );
  } );

  const candidates = Array.from(
    new Set(
      candidateSpaceIds
        .map( ( value ) => String( value || '' ).trim() )
        .filter( Boolean )
    )
  );

  const resolvedScopes = new Set<ScopedSection>();

  SCOPED_SECTIONS.forEach( ( scope ) => {
    for( const candidate of candidates ) {
      const appliedPrimitive = applyAliasesForScope( configMap, scope, candidate );
      const appliedObject = applyObjectAliasesForScope( configMap, scope, candidate );

      if( appliedPrimitive + appliedObject > 0 ) {
        resolvedScopes.add( scope );
        break;
      }
    }
  } );

  // Fallback: if a scope was not resolved by candidate IDs and there is a single
  // space UUID for that scope in the payload, resolve it automatically.
  SCOPED_SECTIONS.forEach( ( scope ) => {
    if( resolvedScopes.has( scope ) )
      return;

    const scopedSpaceIds = new Set<string>();
    Object.keys( configMap ).forEach( ( key ) => {
      const match = key.match( SCOPED_KEY_REGEX );
      const objectMatch = key.match( SCOPED_OBJECT_REGEX );

      if( match && match[2] === scope ) {
        scopedSpaceIds.add( match[1] );
      }

      if( objectMatch && objectMatch[2] === scope ) {
        scopedSpaceIds.add( objectMatch[1] );
      }
    } );

    if( scopedSpaceIds.size !== 1 )
      return;

    const [singleSpaceId] = Array.from( scopedSpaceIds );
    applyAliasesForScope( configMap, scope, singleSpaceId );
    applyObjectAliasesForScope( configMap, scope, singleSpaceId );
  } );

  // Regenerate per-language aliases after resolving scoped keys.
  // This keeps access patterns like hello-es/legal-conditions-es working
  // even when the backend returns object config values directly.
  applyLanguageAliases( configMap );
}
